import { useLazyQuery } from "@apollo/client";
import { MEN_ONLY_SESSION_NOTE, POC_ONLY_SESSION_NOTE } from "@app/shared/constants";
import {
    SanghaSessionCycle,
    Session,
    SessionStarted,
    SessionStatus,
    SessionType,
    UserProfile,
} from "@app/shared/types";
import { dateTimeFromString } from "@app/shared/utils";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Chip, IconButton, Menu, MenuItem } from "@mui/material";
import { alpha, Button, CircularProgress, darken, Link, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { externalImageSrc, localImageSrc } from "app/images";
import { GRAPHQL_QUERY_EVENT_RSVP_USERS } from "app/queries";
import { routes } from "app/routes";
import clsx from "clsx";
import EventCard from "components/EventCard";
import { selectIsAdmin, selectIsTeacher, selectLoggedInTeacherId } from "features/auth/auth";
import { LoggedInUserProfileContext } from "features/auth/RequireAuthentication";
import { TeacherDialog } from "features/community/TeacherDialog";
import { SanghaDayAndTimeDescription } from "features/sangha/SanghaDayAndTimeDescription";
import lotusIcon from "images/icons/lotus-dark.svg";
import _ from "lodash";
import { DateTime } from "luxon";
import { Fragment, useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useDispatch, useSelector } from "react-redux";
import { JoinInterestGroupButton } from "../interestGroups/JoinInterestGroupButton";
import { getInterestGroupConfirmationDialogs } from "../sangha/getInterestGroupConfirmationDialogs";
import { isSessionStarted } from "../sangha/isSessionStarted";
import { ConfirmationDialogProps } from "./ButtonWithConfirmationDialogs";
import { RsvpMenuItem } from "./RsvpMenuItem";
import { getTitle, renderSessionDate, renderSessionTime, renderSessionTimezone } from "./shared";
import { WhosAttendingButton } from "./WhosAttendingButton";
import { ZoomButton } from "./ZoomButton";
import badgeDedicatedIcon from "images/icons/badge-dedicated-icon.svg";
import badgeDropInIcon from "images/icons/badge-dropin-icon.svg";
import { push } from "connected-react-router";
import { WhosAttendingMenuItem } from "./WhosAttendingMenuItem";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { AddToCalendarMenuItem } from "./AddToCalendarMenuItem";
import { LeaveGroupMenuItem } from "./LeaveGroupMenuItem";
import { RsvpButton } from "./RsvpButton";
import { RequestSubstituteMenuItem } from "features/teacherSubstitutes/RequestSubstituteMenuItem";

const useStyles = makeStyles((theme) => ({
    card: {
        padding: theme.spacing(4),
    },
    eventTime: {
        marginBottom: 0,
    },
    cancelledCard: {
        "& $eventTime": {
            textDecoration: "line-through",
        },
    },
    activeCard: {
        backgroundColor: darken(theme.palette.primary500, 0.1),
        backgroundSize: "cover",
        backgroundPosition: "50% 0%",
        backgroundImage: `url(${localImageSrc("bg2-min.jpg", 800)})`,
        color: theme.palette.coral100,

        "& $whosAttendingButton": {
            backgroundColor: alpha(theme.palette.grey700, 0.3),
            color: theme.palette.coral100,

            "&:hover": {
                backgroundColor: alpha(theme.palette.grey700, 0.4),
            },
        },
        "& $chatButton": {
            color: theme.palette.coral100,
            backgroundColor: theme.palette.grey400,
        },
    },
    contentWrapper: {
        display: "flex",
    },
    cancelledSessionMessage: {
        textAlign: "right",
        marginTop: theme.spacing(5),
    },
    fadeCarousel: {
        width: "100%",
        height: "100%",
    },
    name: {
        display: "inline-block",
        margin: 0,
        marginRight: theme.spacing(1),
    },
    teacherName: {
        margin: 0,
        display: "inline-block",
        [theme.breakpoints.down("lg")]: {
            marginBottom: theme.spacing(2),
        },
    },
    teacherLink: {
        color: "inherit",
    },
    with: {
        display: "inline-block",
        marginRight: theme.spacing(1),
        marginBottom: 0,
        [theme.breakpoints.down("lg")]: {
            marginBottom: theme.spacing(2),
        },
    },
    and: {
        display: "inline-block",
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },

    icon: {
        maxWidth: theme.spacing(5),
    },
    col1: {
        display: "inline-block",
        marginRight: theme.spacing(2),
        marginTop: 0,
        marginBottom: 0,
        [theme.breakpoints.down("md")]: {
            display: "block",
            marginRight: theme.spacing(0),
        },
    },
    col2: {
        display: "inline-block",
        [theme.breakpoints.down("md")]: {
            display: "block",
        },
    },
    description: {
        display: "inline-block",
        marginRight: theme.spacing(2),
    },
    buttons: {
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },

    buttonWrapper: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(2, 2),

        "& *": {
            flex: "0 0 auto",
        },

        [theme.breakpoints.up("lg")]: {
            // width: 430,
            flexWrap: "nowrap",
            width: "max-content",
        },

        [theme.breakpoints.down("md")]: {
            justifyContent: "left",
        },
    },
    rsvpButton: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    chatButton: {
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    chatMenuItem: {
        marginBottom: 0,
    },
    whosAttendingButton: {
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    confirmationDialogTitle: {
        marginBottom: theme.spacing(5),
    },
    additionalInfo: {},
    dedicatedColors: {
        backgroundColor: alpha(theme.palette.aqua700, 0.2),
        color: theme.palette.aqua700,
    },
    dropInColors: {
        backgroundColor: alpha(theme.palette.coral300, 0.2),
        color: theme.palette.coral400,
    },
    menu: {
        borderRadius: theme.borderRadius.default,
        backgroundColor: theme.palette.background.default,
        overflow: "visible",
    },
    menuItem: {
        marginBottom: 0,
        "&:hover": {
            backgroundColor: alpha(theme.palette.grey700, 0.1),
        },
    },
    menuButton: {
        position: "absolute",
        // TODO hacky way to position the menu button - should be set up to align to the parent element of the whole card instead of only the content area.
        top: theme.spacing(-2.5),
        right: 0,
    },
    menuIcon: {
        fontSize: theme.typography.pxToRem(35),
    },
    menuItemIcon: {
        marginRight: theme.spacing(2),
    },
    cardContentContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "end",
        gap: theme.spacing(2),
        minHeight: 100,
        [theme.breakpoints.down("lg")]: {
            paddingRight: theme.spacing(6),
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0,
        },
        [theme.breakpoints.down("sm")]: {
            paddingRight: 0,
        },
    },
    cardContentMain: {
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.up("lg")]: {
            width: 340,
        },
    },
    cardContentTime: {
        [theme.breakpoints.up("lg")]: {
            width: "min-content",
            flexGrow: 1,
        },
        [theme.breakpoints.down("lg")]: {
            marginBottom: theme.spacing(2),
        },
    },
    eventName: {
        margin: 0,
        [theme.breakpoints.down("lg")]: {
            marginBottom: theme.spacing(1),
        },
        [theme.breakpoints.down("sm")]: {
            marginTop: theme.spacing(1),
        },
    },
    spacer: {
        flexGrow: 1,
        [theme.breakpoints.down("lg")]: {
            display: "none",
        },
    },
}));

export const CommunityEventCard = (props: {
    event: Session;
    timezone: string;
    isPublic?: boolean;
    currentServerTime: DateTime;
    customButtons?: React.ReactNode;
    additionalInfo?: string;
    showLeaveGroupOptions?: boolean;
    showSpecificDate?: boolean;
}): JSX.Element => {
    const classes = useStyles();
    const { event, timezone, currentServerTime, customButtons, showSpecificDate } = props;
    const isTeacher = useSelector(selectIsTeacher);
    const loggedInTeacherId = useSelector(selectLoggedInTeacherId);
    const isAdmin = useSelector(selectIsAdmin);
    const loggedInUser = useContext(LoggedInUserProfileContext);

    const dispatch = useDispatch();

    const [dialogActiveForTeacherId, setDialogActiveForTeacherId] = useState<string | null>(null);

    const [loadRsvpUsers, rsvpUsersQueryStatus] = useLazyQuery(GRAPHQL_QUERY_EVENT_RSVP_USERS, {
        variables: { id: event.id },
    });

    const rsvpUsers = (rsvpUsersQueryStatus.data?.event?.rsvpUsers || []) as UserProfile[];
    const userIsPartOfGroup = _.some(rsvpUsers, (x) => x.id === loggedInUser.id);

    const userIsTeacherOfEvent =
        isTeacher &&
        _.includes(
            event.teachers.map((x) => x.id),
            loggedInTeacherId,
        );

    const { ref, inView } = useInView({
        threshold: 0,
        triggerOnce: true,
    });
    useEffect(() => {
        if (inView && !customButtons) {
            loadRsvpUsers();
        }
    }, [inView, customButtons]);

    const sessionStartedStatus = isSessionStarted({
        sessionStart: dateTimeFromString(event.time, timezone),
        sessionEnd: dateTimeFromString(event.endTime, timezone),
        currentServerTime,
    });

    const eventStartedOrStartingSoon =
        sessionStartedStatus === SessionStarted.IN_PROGRESS ||
        sessionStartedStatus === SessionStarted.ABOUT_TO_START;

    const isActiveEventCard = !props.isPublic && !event.cancelled && eventStartedOrStartingSoon;

    const isGroupSession = [SessionType.SANGHA_SESSION, SessionType.INTEREST_GROUP].includes(
        event.type,
    );
    const isInterestGroup = event.type === SessionType.INTEREST_GROUP;

    useEffect(() => {
        // when the card reloads, make sure to reinitialize the AddEvent library
        (window as any).addeventatc?.refresh();
    }, []);

    const getConfirmationDialogs = (): ConfirmationDialogProps[] => {
        if (
            event.type === SessionType.SILENT_MEDITATION_HALL ||
            event.type === SessionType.SILENT_SIT
        ) {
            return [
                {
                    enableConfirmButtonDelay: 0,
                    button: "Enter",
                    content: (
                        <>
                            <img src={lotusIcon} alt="" />
                            <Typography variant="h3" className={classes.confirmationDialogTitle}>
                                A space for noble silence
                            </Typography>
                            <Typography variant="body1">
                                You are entering the silent meditation hall. Please leave your
                                microphone muted and maintain noble silence during your time in this
                                sacred space.
                            </Typography>
                        </>
                    ),
                },
            ];
        }
        if (event.type === SessionType.INTEREST_GROUP && event.sangha) {
            return getInterestGroupConfirmationDialogs(event.sangha.id);
        }
        if (event.notes === POC_ONLY_SESSION_NOTE) {
            return [
                {
                    localStorageKey: "pocReminderSeenV2",
                    button: "I identify as a person of color",
                    content: (
                        <>
                            <img src={lotusIcon} alt="" />
                            <Typography variant="h3" className={classes.confirmationDialogTitle}>
                                A space for people of color
                            </Typography>
                            <Typography variant="body1">
                                This is a space specifically for people of color. For those of us
                                with marginalized identities, it can be important to have spaces in
                                which we can relate to one another through the lens of our unique
                                cultural and societal experiences, to help cultivate safety and
                                healing.
                            </Typography>

                            <Typography variant="body1">
                                As a reminder, people of all backgrounds who do not identify as
                                white are welcome to participate, and ethnicity is not always
                                visually evident.
                            </Typography>
                        </>
                    ),
                },
            ];
        }
        if (event.notes === MEN_ONLY_SESSION_NOTE) {
            return [
                {
                    localStorageKey: "menOnlyReminderSeenV2",
                    button: "Continue",
                    content: (
                        <>
                            <img src={lotusIcon} alt="" />
                            <Typography variant="h3" className={classes.confirmationDialogTitle}>
                                A space for men to explore, share and connect
                            </Typography>
                            <Typography variant="body1">
                                These sessions offer a safe space for breaking cultural notions
                                around masculinity, creating open spaces for all voices to be heard,
                                and tearing down the walls of separation (self vs other).
                            </Typography>
                        </>
                    ),
                },
            ];
        }

        return [];
    };

    const renderZoomButton = (isStartButton: boolean) => {
        return (
            <ZoomButton
                isStartButton={isStartButton}
                event={event}
                sessionStartedStatus={sessionStartedStatus}
                confirmationDialogs={isStartButton ? undefined : getConfirmationDialogs()}
                variant={isActiveEventCard ? "secondaryInverse" : "secondary"}
                iconHeight={20}
            />
        );
    };
    const renderJoinButton = () => renderZoomButton(false);
    const renderStartButton = () => renderZoomButton(true);

    const renderTime = (event: Session) => {
        return renderSessionTime(event, timezone);
    };

    const renderTimezone = (event: Session) => {
        return renderSessionTimezone(event, timezone);
    };

    const renderWhosAttending = (isButton: boolean) => {
        if (!event.cancelled) {
            if (rsvpUsersQueryStatus.loading) {
                return <CircularProgress />;
            }

            return isButton ? (
                <WhosAttendingButton
                    rsvpUsers={rsvpUsers}
                    event={event}
                    renderEventTime={renderTime}
                    classes={{ button: classes.whosAttendingButton }}
                />
            ) : (
                <WhosAttendingMenuItem
                    rsvpUsers={rsvpUsers}
                    event={event}
                    renderEventTime={renderTime}
                    classes={{ button: classes.whosAttendingButton }}
                />
            );
        }
    };

    const renderChatButton = () => {
        // TODO: might be nice to actually render count of unread messages
        return (
            <Button
                href={routes.memberSanghaDetails(event.sangha?.id || "")}
                variant="secondary"
                size="small"
                classes={{ root: classes.chatButton }}
            >
                Chat with group
            </Button>
        );
    };

    const renderChatMenuItem = () => {
        return (
            <MenuItem
                data-testid="chatWithGroupMenuItem"
                onClick={() => dispatch(push(routes.memberSanghaDetails(event.sangha?.id || "")))}
                className={classes.chatMenuItem}
            >
                Chat with group
            </MenuItem>
        );
    };

    const renderRsvpComponent = (isButton: boolean) => {
        if (event.type === SessionType.SILENT_MEDITATION_HALL) {
            return null;
        }

        if (rsvpUsersQueryStatus.loading) {
            return <CircularProgress />;
        }

        if (isButton) {
            return <RsvpButton rsvpUsers={rsvpUsers} event={event} />;
        } else {
            return <RsvpMenuItem rsvpUsers={rsvpUsers} event={event} />;
        }
    };

    const renderAddToCalendarMenuItem = () => {
        if (!isGroupSession) {
            // Only show "add to calendar" for groups - we show the RSVP option for other events
            return null;
        }

        if (!userIsPartOfGroup && !userIsTeacherOfEvent) {
            // Only show the add to calendar item for group members and the group teacher
            return null;
        }

        const isWeeklyEvent = event.sangha?.schedule.cycle === SanghaSessionCycle.weekly;
        // We know the teacher is subbing if they are different from the groups regular teacher
        const isSubstituteTeacher =
            event.sangha && userIsTeacherOfEvent && event.sangha.teacher.id !== loggedInTeacherId;

        return (
            <AddToCalendarMenuItem
                event={event}
                // Only support recurrance for weekly groups, this is more complicated for monthly and twice weekly groups.
                // For now these will only add this one session to the users calendar
                // For substitute teachers we want to add only a single session, and not make it recurring
                isRecurringEvent={isWeeklyEvent && !isSubstituteTeacher}
            />
        );
    };

    const renderLeaveGroupMenuItem = () => {
        if (rsvpUsersQueryStatus.loading) {
            return <CircularProgress />;
        }

        if (userIsPartOfGroup && event.sangha && event.sanghaMembership) {
            return (
                <LeaveGroupMenuItem
                    sangha={event.sangha}
                    sanghaMembershipId={event.sanghaMembership.id}
                    eventId={event.id}
                />
            );
        }
    };

    const renderButtons = () => {
        if (props.customButtons) {
            return <>{props.customButtons}</>;
        }

        if (isInterestGroup && !(isTeacher || isAdmin)) {
            if (rsvpUsersQueryStatus.loading) {
                return <CircularProgress />;
            } else {
                if (!userIsPartOfGroup) {
                    return event.sangha ? (
                        <JoinInterestGroupButton
                            interestGroup={event.sangha}
                            variant={isActiveEventCard ? "primaryInverse" : "primary"}
                        />
                    ) : null;
                }
            }
        }

        return (
            <>
                {isGroupSession && !(isTeacher || isAdmin) && renderChatButton()}
                {(isTeacher || isAdmin) && renderStartButton()}
                {eventStartedOrStartingSoon && renderJoinButton()}
                {!eventStartedOrStartingSoon &&
                    event.type !== SessionType.INTEREST_GROUP &&
                    event.type !== SessionType.SANGHA_SESSION &&
                    renderRsvpComponent(true)}
            </>
        );
    };

    const renderTeacher = () =>
        event.teachers &&
        event.teachers[0] && (
            <>
                <div>
                    <Typography variant="caption" className={classes.with}>
                        with
                    </Typography>

                    {event.teachers.map((teacher, index) => (
                        <Fragment key={teacher.id}>
                            {index > 0 && (
                                <Typography variant="caption" className={classes.and}>
                                    and
                                </Typography>
                            )}

                            <Typography
                                key={teacher.id}
                                variant="h4"
                                className={classes.teacherName}
                            >
                                <Button
                                    variant="tertiary"
                                    onClick={() => setDialogActiveForTeacherId(teacher.id)}
                                    className={classes.teacherLink}
                                >
                                    {teacher.name}
                                </Button>
                            </Typography>
                        </Fragment>
                    ))}
                </div>
                <TeacherDialog
                    open={Boolean(dialogActiveForTeacherId)}
                    onClose={() => setDialogActiveForTeacherId(null)}
                    teacher={
                        event.teachers.find((x) => x.id === dialogActiveForTeacherId) ||
                        event.teachers[0]
                    }
                />
            </>
        );

    const renderEventTime = () => {
        if (event.type === SessionType.SILENT_MEDITATION_HALL) {
            return <>Open 24/7</>;
        }

        if (event.type === SessionType.INTEREST_GROUP && event.sangha && !showSpecificDate) {
            return (
                <>
                    <SanghaDayAndTimeDescription
                        sangha={event.sangha}
                        userTimeZone={timezone}
                        overrideStartTime={DateTime.fromISO(event.time)}
                        overrideEndTime={DateTime.fromISO(event.endTime)}
                        renderTimeZone={true}
                    />
                </>
            );
        }
        return (
            <>
                {renderSessionDate(event, timezone)}
                <br />
                {renderTime(event)} {renderTimezone(event)}
            </>
        );
    };

    const handleChangeGroupParticipation = ({ sangha }: Session) => {
        if (!sangha) {
            throw new Error("No sangha found");
        }

        const isSwitchingGroupParticipation = true;
        dispatch(
            push({
                pathname: routes.memberConfirmInterestGroup(sangha.id),
                state: { isSwitchingGroupParticipation },
            }),
        );
    };

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
        setMenuAnchor(event.currentTarget);
    const handleMenuClose = () => setMenuAnchor(null);

    const renderMenuButton = () => {
        const isUserSignedUp = event.sanghaMembership;

        return (
            <>
                <IconButton
                    color="secondary"
                    size="small"
                    aria-label="menu"
                    aria-controls="card-menu"
                    aria-haspopup="true"
                    onClick={handleMenuOpen}
                    className={classes.menuButton}
                    data-testid="cardMenuButton"
                >
                    <MoreHorizIcon className={classes.menuIcon} />
                </IconButton>
                <Menu
                    id="card-menu"
                    anchorEl={menuAnchor}
                    keepMounted={false}
                    open={!!menuAnchor}
                    onClose={handleMenuClose}
                    classes={{ paper: classes.menu }}
                >
                    {/* Chat with group menu item for teachers */}
                    {/* We will show this menu item for teachers&admins to have more space in the card for the "Start session" button */}
                    {isGroupSession && (isTeacher || isAdmin) && renderChatMenuItem()}
                    {/* See participants menu item */}
                    {renderWhosAttending(false)}
                    {/* Add to calendar menu item */}
                    {renderAddToCalendarMenuItem()}
                    {/* Change group participation menu item */}
                    {isInterestGroup && isUserSignedUp && (
                        <MenuItem
                            onClick={() => {
                                handleChangeGroupParticipation(event);
                                handleMenuClose();
                            }}
                            className={classes.menuItem}
                            data-testid="changeGroupParticipationLink"
                        >
                            <ManageAccountsOutlinedIcon className={classes.menuItemIcon} /> Change
                            group participation
                        </MenuItem>
                    )}
                    {/* Leave group menu item */}
                    {isInterestGroup && renderLeaveGroupMenuItem()}
                    {(userIsTeacherOfEvent || isAdmin) && (
                        <RequestSubstituteMenuItem
                            session={event}
                            onClose={() => handleMenuClose()}
                            classes={{ menuItem: classes.menuItem }}
                        />
                    )}
                </Menu>
            </>
        );
    };

    return (
        <>
            <EventCard
                containerAttributes={{
                    ref: ref,
                    "data-testid": "communityEventCard",
                    "data-session-status": event.cancelled
                        ? SessionStatus.CANCELED
                        : SessionStatus.PUBLISHED,

                    "data-session-id": event.id,
                    "data-group-id": event.sangha?.id,
                    id: event.id, // this is for the scroll functionality - please do not remove
                }}
                cancelled={event.cancelled}
                pictures={
                    event.type === SessionType.SILENT_MEDITATION_HALL ||
                    event.type === SessionType.SILENT_SIT
                        ? [localImageSrc(`community/bowl.png`, 260)]
                        : event.teachers.map((x) => externalImageSrc(x.picture ?? "", 260))
                }
                information={
                    <>
                        {renderMenuButton()}
                        <div className={classes.cardContentContainer}>
                            <div className={classes.cardContentMain}>
                                {event.sanghaMembership &&
                                event.type === SessionType.INTEREST_GROUP ? (
                                    event.sanghaMembership?.isDropIn === true ? (
                                        <Chip
                                            variant="pink"
                                            size="small"
                                            label="Drop-in Group Participant"
                                            icon={<img src={badgeDropInIcon} alt="drop-in icon" />}
                                            data-testid="commitmentBadge"
                                        />
                                    ) : (
                                        <Chip
                                            variant="blue"
                                            size="small"
                                            label="Dedicated Group Participant"
                                            icon={
                                                <img
                                                    src={badgeDedicatedIcon}
                                                    alt="dedicated icon"
                                                />
                                            }
                                            data-testid="commitmentBadge"
                                        />
                                    )
                                ) : (
                                    <div className={classes.spacer} />
                                )}
                                <Typography variant="h3" className={classes.eventName}>
                                    {getTitle(event)}
                                </Typography>
                                {/* silent sits do not have a teacher */}
                                {event.type !== SessionType.SILENT_SIT &&
                                    event.type !== SessionType.SILENT_MEDITATION_HALL &&
                                    renderTeacher()}
                            </div>

                            <div className={classes.cardContentTime}>
                                <Typography variant="body1" className={classes.eventTime}>
                                    {renderEventTime()}{" "}
                                </Typography>

                                {(isTeacher || isAdmin) && event.substituteRequested && (
                                    <Typography
                                        variant="caption"
                                        className={classes.additionalInfo}
                                    >
                                        Substitute requested
                                    </Typography>
                                )}

                                {props.additionalInfo && (
                                    <Typography
                                        variant="caption"
                                        className={classes.additionalInfo}
                                    >
                                        {props.additionalInfo}{" "}
                                    </Typography>
                                )}
                            </div>
                            <div className={classes.buttons}>
                                {event.cancelled ? (
                                    <div className={classes.cancelledSessionMessage}>
                                        <Typography variant="h4">Session cancelled</Typography>
                                    </div>
                                ) : (
                                    <div className={classes.buttonWrapper}>
                                        {!props.isPublic && renderButtons()}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                }
                // actionBar={}
                classes={{
                    card: clsx(classes.card, {
                        [classes.cancelledCard]: event.cancelled,
                        [classes.activeCard]: isActiveEventCard,
                    }),
                    contentWrapper: classes.contentWrapper,
                }}
            />
        </>
    );
};

export default CommunityEventCard;
